/**
 * Window-singleton resolver for custom server emoji.
 *
 * The markdown engine renders `:name:` tokens by looking up against this
 * registry. We avoid prop-drilling the current guild's emoji map down
 * to every message bubble — and we avoid re-rendering message lists on
 * emoji change (the markdown re-walks anyway).
 *
 * Lifecycle:
 *   - on guild switch / GET /api/guilds/:id/emojis → setGuildEmojis()
 *   - emoji:add / emoji:remove socket events patch the map in place
 *   - markdown engine reads via resolveEmoji()
 */

import type { Emoji } from '@/types/api';

let currentMap: Map<string, Emoji> = new Map();
const listeners = new Set<() => void>();

/** Replace the active guild's emoji list. */
export function setGuildEmojis(emojis: Emoji[]): void {
  currentMap = new Map(emojis.map((e) => [e.name, e]));
  for (const fn of listeners) fn();
}

export function addEmoji(emoji: Emoji): void {
  currentMap.set(emoji.name, emoji);
  for (const fn of listeners) fn();
}

export function removeEmoji(emojiId: string): void {
  for (const [name, e] of currentMap) {
    if (e.id === emojiId) {
      currentMap.delete(name);
      break;
    }
  }
  for (const fn of listeners) fn();
}

export function clearEmojis(): void {
  if (currentMap.size === 0) return;
  currentMap = new Map();
  for (const fn of listeners) fn();
}

export function resolveEmoji(name: string): Emoji | undefined {
  return currentMap.get(name);
}

export function listEmojis(): Emoji[] {
  return [...currentMap.values()];
}

export function subscribeEmojis(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
